import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CredentialTestResult, IntegrationEntry, SavedCredential, saveIntegrationCredential, testCredentialApi,
} from '../../api/admin';
import { Modal } from '../../components/fs';

/**
 * Adding a credential, in four steps.
 *
 * A bare "API token" box next to a Save button teaches nobody where to find
 * the token, and never tells you whether it works — so the flow is:
 *
 *   1  which service, grouped and searchable, with the nine that hold nothing
 *      visible and inert beside the reason;
 *   2  the fields, generated from the registry with its real labels,
 *      placeholders and hints, plus the integration's own docs link;
 *   3  TEST IT — a read-only probe, reported in lines, whose failure text
 *      names the setting to change. This step is why it is a wizard;
 *   4  what it changes, in the account's own terms.
 *
 * `Save anyway` survives a failed test on purpose. A refused test is not a
 * refusal to save: the service may be down, or the token may be minted five
 * minutes from now, and a wizard that would not let you past would be a wizard
 * people learn to route around.
 *
 * Built on the existing stepped-modal shell — `fs-modal wide`, "Step n of 4"
 * in the head, `fs-steps` dots in the footer — the same one the follow-up
 * setup flow uses.
 */
export default function CredentialWizard({ entries, provider, replacing, onClose, onSaved }: {
  entries: IntegrationEntry[];
  /** skip step 1 when the card already said which service */
  provider?: string;
  /** the credential being replaced, when this is "Replace token…" */
  replacing?: SavedCredential;
  onClose: () => void;
  onSaved: (title: string) => void;
}) {
  const [step, setStep] = useState<1 | 2 | 3 | 4>(provider ? 2 : 1);
  const [picked, setPicked] = useState(provider || '');
  const [values, setValues] = useState<Record<string, string>>({});
  const [label, setLabel] = useState(replacing?.label || '');
  const [test, setTest] = useState<CredentialTestResult | null>(null);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  const entry = entries.find((e) => e.type === picked) || null;
  const fields = entry?.fields || [];

  const groups = useMemo(() => {
    const q = search.trim().toLowerCase();
    const match = (e: IntegrationEntry) => !q || e.title.toLowerCase().includes(q);
    const byCategory = new Map<string, IntegrationEntry[]>();
    for (const e of entries.filter((x) => x.fields).filter(match)) {
      const list = byCategory.get(e.category) || [];
      list.push(e);
      byCategory.set(e.category, list);
    }
    return {
      holders: [...byCategory.entries()],
      // Listed, inert, with the reason — never quietly missing, which is what
      // sent people looking for a Slack token that does not exist.
      passthroughs: entries.filter((e) => !e.fields).filter(match),
    };
  }, [entries, search]);

  async function runTest() {
    if (!entry || testing) return;
    setTesting(true); setError('');
    try {
      const r = await testCredentialApi(entry.type, values, label);
      setTest(r.result);
      setStep(3);
    } catch (e: any) { setError(e.message); } finally { setTesting(false); }
  }

  async function save() {
    if (!entry || saving) return;
    setSaving(true); setError('');
    try {
      await saveIntegrationCredential(entry.type, values, label);
      onSaved(entry.title);
      setStep(4);
    } catch (e: any) { setError(e.message); } finally { setSaving(false); }
  }

  const title = step === 1 ? 'Add credentials'
    : step === 4 ? `${entry?.title} is connected`
      : <>{entry?.icon} {entry?.title} credentials</>;

  const filled = fields.every((f) => (
    // A secret already stored may be left blank on a replace — blank means
    // "keep what is saved", which is the behaviour the old screen had and the
    // reason changing a slug alone cannot wipe the token beside it.
    values[f.key]?.trim() || (replacing && f.secret && replacing.secretsSet.includes(f.key))
  ));

  return (
    <Modal
      title={title}
      wide
      aside={`Step ${step} of 4`}
      onClose={onClose}
      footer={
        <>
          {step === 1 && (
            <button className="fs-btn primary" disabled={!picked} onClick={() => setStep(2)}>Continue</button>
          )}
          {step === 2 && (
            <button className="fs-btn primary" disabled={!filled || testing} onClick={runTest}>
              {testing ? 'Testing…' : 'Test connection'}
            </button>
          )}
          {step === 3 && (
            <button className="fs-btn primary" disabled={saving} onClick={save}>
              {test?.ok ? 'Save credentials' : 'Save anyway'}
            </button>
          )}
          {step === 4 && (
            <>
              <button className="fs-btn primary" onClick={onClose}>Done</button>
              <button
                className="fs-btn neutral"
                onClick={() => {
                  setStep(1); setPicked(''); setValues({}); setLabel(''); setTest(null);
                }}
              >
                Add another integration
              </button>
            </>
          )}
          {step > 1 && step < 4 && (
            <button
              className="fs-btn neutral"
              onClick={() => setStep(step === 3 ? 2 : 1)}
              disabled={step === 2 && !!provider}
            >
              {step === 3 ? 'Back to credentials' : 'Back'}
            </button>
          )}
          {step < 4 && <button className="fs-btn neutral" onClick={onClose}>Cancel</button>}
          <span style={{ flex: 1 }} />
          <span className="fs-steps">
            {[1, 2, 3, 4].map((n) => <i key={n} className={n <= step ? 'done' : ''} />)}
          </span>
        </>
      }
    >
      {error && <div className="ad-err" style={{ marginBottom: 12 }}>{error}</div>}

      {/* ---- 1: which service ---- */}
      {step === 1 && (
        <>
          <input
            className="fs-input" autoFocus placeholder="Search integrations…"
            value={search} onChange={(e) => setSearch(e.target.value)}
            style={{ marginBottom: 14 }}
          />
          {groups.holders.map(([category, list]) => (
            <div key={category} style={{ marginBottom: 14 }}>
              <div className="fs-rail-cap" style={{ padding: 0 }}>{CATEGORY_LABEL[category] || category}</div>
              {list.map((e) => (
                <label key={e.type} className={`fs-choice${picked === e.type ? ' on' : ''}`}>
                  <input type="radio" checked={picked === e.type} onChange={() => setPicked(e.type)} />
                  <span>
                    <b>{e.icon} {e.title}</b>
                    <small>
                      {e.credentials.length
                        ? `Connected · add a second ${workspaceWord(e.type)}`
                        : (e.fields || []).map((f) => f.label).join(' · ')}
                    </small>
                  </span>
                </label>
              ))}
            </div>
          ))}

          {!!groups.passthroughs.length && (
            <div>
              <div className="fs-rail-cap" style={{ padding: 0 }}>Not set up here</div>
              {groups.passthroughs.map((e) => (
                <label key={e.type} className="fs-choice" aria-disabled style={{ opacity: 0.6 }}>
                  <input type="radio" disabled checked={false} onChange={() => undefined} />
                  <span>
                    <b>{e.icon} {e.title}</b>
                    <small>{e.holdsNothing}</small>
                  </span>
                </label>
              ))}
            </div>
          )}
        </>
      )}

      {/* ---- 2: the fields ---- */}
      {step === 2 && entry && (
        <>
          {fields.map((f) => {
            const stored = replacing?.secretsSet.includes(f.key) || (replacing?.config?.[f.key] !== undefined);
            return (
              <div key={f.key} className="fs-field" style={{ marginBottom: 14 }}>
                <label htmlFor={`cred-${f.key}`}>
                  {f.label}
                  {f.secret && stored && <span className="fs-cell-sub faint"> (one is saved)</span>}
                </label>
                {/* A subdomain typed in front of a fixed host reads as one
                    thing, so it is drawn as one. */}
                {SUFFIX[entry.type]?.[f.key] ? (
                  <div className="fs-suffixed">
                    <input
                      id={`cred-${f.key}`} type="text" placeholder={f.placeholder}
                      value={values[f.key] || ''}
                      onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                    />
                    <span className="suffix">{SUFFIX[entry.type][f.key]}</span>
                  </div>
                ) : (
                  <input
                    id={`cred-${f.key}`}
                    type={f.secret ? 'password' : 'text'}
                    placeholder={f.secret && stored ? 'Enter a new value to replace the saved one' : f.placeholder}
                    value={values[f.key] || ''}
                    onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                  />
                )}
                {f.hint && <div className="hint">{f.hint}</div>}
              </div>
            );
          })}

          {entry.docs && (
            <div className="fs-cell-sub">
              <a href={entry.docs} target="_blank" rel="noreferrer">API reference ↗</a>
            </div>
          )}

          <div className="fs-consequence" style={{ marginTop: 14 }}>
            Stored encrypted and never sent back to a browser. Every form on this account can
            <b> use</b> it; only Owners and Admins can see that it exists or replace it.
            {replacing && (
              <div style={{ marginTop: 8 }}>
                Leaving a saved field blank keeps what is stored — only what you type is sent,
                so changing one field cannot wipe the one beside it.
              </div>
            )}
          </div>
        </>
      )}

      {/* ---- 3: the test, and the optional name ---- */}
      {step === 3 && entry && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 12 }}>
            <span className={`fs-pill ${test?.ok ? 'ok' : 'danger'}`}>{test?.ok ? 'reached' : 'refused'}</span>
            <span className="fs-cell-sub">
              {test?.target} answered{test?.ms ? ` in ${test.ms}ms` : ''}
            </span>
          </div>

          {(test?.checks || []).map((c, i) => (
            <div key={i} style={{ display: 'flex', gap: 9, padding: '6px 0', alignItems: 'flex-start' }}>
              <span style={{ color: c.ok ? 'var(--ok-fg)' : 'var(--danger)', fontWeight: 700 }}>
                {c.ok ? '✓' : '✕'}
              </span>
              <span style={{ fontSize: 'var(--text-body)', color: 'var(--ink-800)', lineHeight: 1.5 }}>{c.text}</span>
            </div>
          ))}

          {test?.ok && (
            <div className="fs-cell-sub faint" style={{ marginTop: 8 }}>
              Read-only check — nothing was created.
            </div>
          )}

          {/* The failure text names the SETTING, not the status code. */}
          {!test?.ok && test?.hint && (
            <div className="fs-consequence warn" style={{ marginTop: 12 }}>
              {test.hint}
              <div style={{ marginTop: 8 }}>
                Nothing has been saved. Go back and change it — or save it anyway and fix it
                later; deliveries will fail until it works.
              </div>
            </div>
          )}

          <div className="fs-field" style={{ marginTop: 18 }}>
            <label htmlFor="cred-label">Name this credential <span className="fs-cell-sub faint">(optional)</span></label>
            <input
              id="cred-label" value={label} onChange={(e) => setLabel(e.target.value)}
              placeholder="Support"
            />
            <div className="hint">
              Only needed if this account uses more than one {entry.title}. A connection then
              picks which by name.
            </div>
          </div>
        </>
      )}

      {/* ---- 4: what it changes ---- */}
      {step === 4 && entry && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 22 }}>{entry.icon}</span>
            <div>
              <b style={{ fontSize: 'var(--text-card-title)' }}>
                {entry.title}{label ? ` · ${label}` : ''}
              </b>
              <div className="fs-cell-sub">saved just now by you</div>
            </div>
          </div>

          <div className="fs-field" style={{ marginTop: 18 }}><label>What this changes</label></div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 'var(--text-body)', lineHeight: 1.7, color: 'var(--ink-800)' }}>
            <li>
              Any form can now add a <b>{entry.title}</b> step in its workflow without asking
              for a token.
            </li>
            {entry.usage.connections > 0 && (
              <li>
                <b>{entry.usage.connections} existing {entry.usage.connections === 1 ? 'connection' : 'connections'}</b>
                {' '}may carry their own {entry.title} token and keep using it — a value set on a
                connection always wins.
              </li>
            )}
            <li>Members with Edit can pick this credential; they cannot read or change it.</li>
          </ul>

          <div className="fs-cell-sub" style={{ marginTop: 14 }}>
            <Link to="/admin" onClick={onClose}>Go to your forms →</Link>
          </div>
        </>
      )}
    </Modal>
  );
}

const CATEGORY_LABEL: Record<string, string> = {
  helpdesk: 'Helpdesk',
  project: 'Project',
  crm: 'CRM',
  data: 'Data',
  chat: 'Chat',
  automation: 'Automation',
  generic: 'Generic',
  workflow: 'Workflow',
};

/** The fixed host a subdomain is typed in front of — see `.fs-suffixed`. */
const SUFFIX: Record<string, Record<string, string>> = {
  zendesk: { subdomain: '.zendesk.com' },
  freshdesk: { domain: '.freshdesk.com' },
  monday: { accountSlug: '.monday.com' },
};

function workspaceWord(type: string): string {
  if (type === 'monday') return 'workspace';
  if (type === 'jira') return 'site';
  if (type === 'zendesk' || type === 'freshdesk') return 'domain';
  return 'account';
}
